import { useEffect } from "react";

const STORAGE_KEY = "oz_traffic_source";

interface TrafficSource {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
  captured_at?: string;
}

/**
 * Captures traffic source from UTM parameters or document referrer
 * and stores it in sessionStorage for use at signup
 */
export function captureTrafficSource(): void {
  // Only capture once per session - don't overwrite if already captured
  if (sessionStorage.getItem(STORAGE_KEY)) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  
  const trafficSource: TrafficSource = {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
    referrer: document.referrer || undefined,
    landing_page: window.location.pathname,
    captured_at: new Date().toISOString(),
  };

  // Only store if we have some source info
  if (trafficSource.utm_source || trafficSource.referrer) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trafficSource));
  }
}

/**
 * Get the stored traffic source data
 */
export function getTrafficSource(): TrafficSource | null {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Get a formatted string representation of the traffic source
 * Priority: UTM source > parsed referrer domain > "direct"
 */
export function getTrafficSourceString(): string {
  const source = getTrafficSource();
  if (!source) return "direct";
  
  // Prefer explicit UTM source
  if (source.utm_source) {
    let result = source.utm_source;
    if (source.utm_medium) result += ` (${source.utm_medium})`;
    if (source.utm_campaign) result += ` - ${source.utm_campaign}`;
    return result;
  }
  
  // Parse referrer domain
  if (source.referrer) {
    try {
      const url = new URL(source.referrer);
      const domain = url.hostname.replace("www.", "");
      
      // Map common referrers to readable names
      if (domain.includes("google")) return "Google Search";
      if (domain.includes("facebook") || domain.includes("fb.com")) return "Facebook";
      if (domain.includes("instagram")) return "Instagram";
      if (domain.includes("youtube")) return "YouTube";
      if (domain.includes("tiktok")) return "TikTok";
      if (domain.includes("twitter") || domain.includes("x.com")) return "Twitter/X";
      if (domain.includes("linkedin")) return "LinkedIn";
      if (domain.includes("bing")) return "Bing Search";
      
      return domain;
    } catch {
      return source.referrer;
    }
  }
  
  return "direct";
}

/**
 * Clear the stored traffic source (call after successful signup)
 */
export function clearTrafficSource(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Hook to capture traffic source on component mount
 */
export function useTrafficSource(): void {
  useEffect(() => {
    captureTrafficSource();
  }, []);
}
